import { Container, Grid, Typography } from "@mui/material";
import moment from "moment-timezone";
import React, { useRef } from "react";
import { useLocation } from "react-router-dom";
import * as Yup from "yup";
import BusinessDisplay from "../../../common/components/data-display/BusinessDisplay.jsx";
import PaginatedSearchResults from "../../../common/components/data-display/PaginatedSearchResults.jsx";
import { ProductInstanceSummary } from "../../../common/components/product/ProductInstanceSummary.jsx";
import ProductQuantityChip from "../../../common/components/product/ProductQuantityChip.jsx";
import ProductSaleFocusDisplay from "../../../common/components/product/ProductSaleFocusDisplay.jsx";
import { useAgedOutContext } from "../../../common/context/AgedOutContext.jsx";
import AgedOutService from "../../../common/service/AgedOutService.js";
import StockLogService from "../../../common/service/StockLogService.js";
import AgedOutStockSearchCriteria from "./AgedOutStockSearchCriteria.jsx";
import ManageAgedOutStockSearchCriteria from "./ManageAgedOutStockSearchCriteria.jsx";

function AgedOutProductInstanceSummary({ item, ...props }) {
  return (
    <ProductInstanceSummaryAdminInventoryView
      item={item}
      sx={{ backgroundColor: "#f9eed4" }}
      {...props}
      data-testid="aged-out-product-instance-summary"
    />
  );
}

function ProductInstanceSummaryAdminInventoryView({ item, ...props }) {
  const { setSelectedProductInstance } = useAgedOutContext();
  return (
    <ProductInstanceSummary
      productInstance={item}
      {...props}
      onClick={() => setSelectedProductInstance(item)}
      summaryComponents={[
        ProductQuantityChip,
        ({ product }) => (
          <BusinessDisplay sx={{ width: 200 }} business={product.supplier} />
        ),
        ProductSaleFocusDisplay,
      ]}
    />
  );
}

const ManageAgedOutStock = () => {
  const { changeMadeFlag } = useAgedOutContext();
  const locationState = useLocation().state;
  const lastSearchedCriteria = locationState?.lastSearchedCriteria;
  const invSearchRef = useRef();
  const agedOutSearchRef = useRef();
  const defaultSearchCriteria = lastSearchedCriteria || {
    searchText: "",
    supplierId: null,
    dateAgedOut: moment().format("YYYY-MM-DD"),
    startDate: moment().format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    status: "SUBMITTED",
    orderBy: "BringToMarket",
    businesses: [],
    saleFocus: "",
    page: 1,
    itemsPerPage: 10,
  };

  React.useEffect(() => {
    if (changeMadeFlag && agedOutSearchRef.current) {
      agedOutSearchRef.current.rerunSearch();
    }
  }, [changeMadeFlag]);

  return (
    <Container sx={{ pt: 1.5 }}>
      <Typography variant="h2" gutterBottom>
        Age Out Inventory
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h4" gutterBottom>
            Inventory
          </Typography>
          <PaginatedSearchResults
            ref={invSearchRef}
            fetchSearchResults={StockLogService.getInventoryForAgeOut}
            ResultDisplayComponent={ProductInstanceSummaryAdminInventoryView}
            SearchCriteriaComponent={ManageAgedOutStockSearchCriteria}
            defaultSearchCriteria={defaultSearchCriteria}
            validationSchema={Yup.object().shape({
              searchText: Yup.string(),
              // Ensure startDate is before endDate
              startDate: Yup.date()
                .nullable()
                .typeError("Invalid Date")
                // Only allow dates after 2000
                .test("4-digit-year-required", "Invalid Date", (value) => {
                  return value ? moment(value).year() > 1000 : true;
                })
                .test(
                  "before-end-date-range",
                  "Start date must be before end date",
                  function (value) {
                    const endDate = this.parent.endDate;
                    if (!value || !endDate) {
                      return true;
                    }
                    return moment(value).isBefore(moment(endDate));
                  }
                ),
              endDate: Yup.date()
                .nullable()
                .typeError("Invalid Date")
                .test("4-digit-year-required", "Invalid Date", (value) => {
                  return value ? moment(value).year() > 1000 : true;
                }),
              businesses: Yup.array(),
            })}
            normalizeSearchCriteria={(criteria) => {
              if (
                criteria.startDate &&
                typeof criteria.startDate !== "string"
              ) {
                criteria.startDate = criteria.startDate.format("YYYY-MM-DD");
              }
              if (criteria.endDate && typeof criteria.endDate !== "string") {
                criteria.endDate = criteria.endDate.format("YYYY-MM-DD");
              }
              return criteria;
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="h4" gutterBottom>
            Aged Out Product
          </Typography>
          <PaginatedSearchResults
            ref={agedOutSearchRef}
            fetchSearchResults={AgedOutService.searchAgedOutStock}
            ResultDisplayComponent={AgedOutProductInstanceSummary}
            SearchCriteriaComponent={AgedOutStockSearchCriteria}
            defaultSearchCriteria={defaultSearchCriteria}
            validationSchema={Yup.object().shape({
              dateAgedOut: Yup.date()
                .required()
                .nullable()
                .typeError("Valid date required"),
            })}
            normalizeSearchCriteria={(criteria) => {
              if (
                criteria.dateAgedOut &&
                typeof criteria.dateAgedOut !== "string"
              ) {
                criteria.dateAgedOut =
                  criteria.dateAgedOut.format("YYYY-MM-DD");
              }
              return criteria;
            }}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default ManageAgedOutStock;
